import {
  campaignPeriodStatusSchema,
  campaignPricingModeSchema,
  DONATION_IMPORT_COLUMN_KINDS,
  DONATION_IMPORT_MAX_BYTES,
} from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { sortedListRequest } from './common/list.js';
import { moneySchema } from './common/money.js';
import { paginated, paginationInputSchema } from './common/pagination.js';
import { fileNameSchema, presignedUploadSchema, uploadTokenSchema } from './common/upload.js';
import { generatedFileSchema } from './generated-file.js';
import { ATTACHMENT_SCAN_STATUSES } from './statement.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
  DONATION_BACKDATE_NOT_PERMITTED: commonErrors.DONATION_BACKDATE_NOT_PERMITTED,
};

/** A season an import may enter, as the picker shows it. */
export const donationImportPeriodSchema = z.object({
  id: z.uuid(),
  status: campaignPeriodStatusSchema,
  seasonYear: z.number().int().nullable(),
  startsAt: z.string(),
  endsAt: z.string(),
});

export type DonationImportPeriod = z.infer<typeof donationImportPeriodSchema>;

/**
 * A campaign the caller's seat may collect for, with the seasons the import's
 * own period gate admits for this caller — the open one, and the closed ones
 * when the caller holds `donation.backdate` — newest first. A campaign with no
 * such season is not listed.
 */
export const donationImportTargetSchema = z.object({
  campaignId: z.uuid(),
  campaignName: z.string(),
  pricingMode: campaignPricingModeSchema,
  periods: z.array(donationImportPeriodSchema),
});

export type DonationImportTarget = z.infer<typeof donationImportTargetSchema>;

export const donationImportTemplateInputSchema = z.object({
  campaignPeriodId: z.uuid(),
  enteredByUserId: z.uuid().optional(),
});

export type DonationImportTemplateInput = z.infer<typeof donationImportTemplateInputSchema>;

/** A donation import's life — docs/decisions/0104 §3–§5. */
export const DONATION_IMPORT_STATUSES = [
  'uploaded',
  'processing',
  'preview',
  'committing',
  'committed',
  'failed',
  'discarded',
] as const;
export const donationImportStatusSchema = z.enum(DONATION_IMPORT_STATUSES);
export type DonationImportStatus = z.infer<typeof donationImportStatusSchema>;

export const DONATION_IMPORT_ROW_OUTCOMES = ['valid', 'refused', 'created'] as const;
export type DonationImportRowOutcome = (typeof DONATION_IMPORT_ROW_OUTCOMES)[number];

export const DONATION_IMPORT_ROW_VIEWS = ['all', ...DONATION_IMPORT_ROW_OUTCOMES] as const;
export type DonationImportRowView = (typeof DONATION_IMPORT_ROW_VIEWS)[number];

export const DONATION_IMPORT_SORT_FIELDS = ['createdAt', 'rowCount'] as const;

export const DONATION_IMPORT_RESOLUTIONS = ['attach', 'create'] as const;

/**
 * Why a row was refused, or what it warns of: the stable code entry threw —
 * a `DomainError`'s code with its `details.reason`, a `FORM_*` issue, or a
 * `DONATION_IMPORT_CELL_*` code — the column it is about, when one is, and
 * the scalar details the screen interpolates into `error.<code>`.
 */
export const donationImportIssueSchema = z.object({
  code: z.string(),
  reason: z.string().nullable(),
  column: z.string().nullable(),
  details: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
});

export type DonationImportIssue = z.infer<typeof donationImportIssueSchema>;

/** A column of the season's template, as the preview heads its rows with. */
export const donationImportColumnSchema = z.object({
  key: z.string(),
  label: z.string(),
  kind: z.enum(DONATION_IMPORT_COLUMN_KINDS),
  required: z.boolean(),
});

export const donationImportSchema = z.object({
  id: z.uuid(),
  campaignId: z.uuid(),
  campaignName: z.string(),
  campaignPeriodId: z.uuid(),
  seasonYear: z.number().int().nullable(),
  periodStatus: campaignPeriodStatusSchema,
  formVersion: z.number().int(),
  status: donationImportStatusSchema,
  fileName: z.string(),
  fileSize: z.string(),
  scanStatus: z.enum(ATTACHMENT_SCAN_STATUSES),
  rowCount: z.number().int(),
  validCount: z.number().int(),
  refusedCount: z.number().int(),
  createdCount: z.number().int(),
  warningCount: z.number().int(),
  failureCode: z.string().nullable(),
  uploadedByUserId: z.uuid(),
  uploadedByName: z.string(),
  enteredByUserId: z.uuid().nullable(),
  commitRequestedAt: z.string().nullable(),
  committedAt: z.string().nullable(),
  failedAt: z.string().nullable(),
  discardedAt: z.string().nullable(),
  /**
   * When retention deleted the raw file and cleared every row's cells — 0104
   * §10. Set, the rows still carry their outcome, amount and issues, and no
   * longer the payer's name or telephone.
   */
  rawPurgedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type DonationImport = z.infer<typeof donationImportSchema>;

/**
 * One import with what its preview screen heads and sums: the template's
 * columns by key and label, and per currency the amount and quantity of the
 * rows entry would take and of those already created, summed by the database.
 */
export const donationImportDetailSchema = donationImportSchema.extend({
  columns: z.array(donationImportColumnSchema),
  totals: z.array(
    z.object({
      outcome: z.enum(['valid', 'created']),
      amount: moneySchema,
      quantity: z.number().int(),
      rowCount: z.number().int(),
    }),
  ),
});

export type DonationImportDetail = z.infer<typeof donationImportDetailSchema>;

export const donationImportRowSchema = z.object({
  id: z.uuid(),
  rowNumber: z.number().int(),
  outcome: z.enum(DONATION_IMPORT_ROW_OUTCOMES),
  refusalStage: z.enum(['preview', 'commit']).nullable(),
  issues: z.array(donationImportIssueSchema),
  warnings: z.array(donationImportIssueSchema),
  payerResolution: z.enum(DONATION_IMPORT_RESOLUTIONS).nullable(),
  ownerResolution: z.enum(DONATION_IMPORT_RESOLUTIONS).nullable(),
  amount: moneySchema.nullable(),
  quantity: z.number().int().nullable(),
  donationId: z.uuid().nullable(),
  /** The row as the file had it, by column key; null once retention has cleared it. */
  cells: z.record(z.string(), z.string()).nullable(),
});

export type DonationImportRow = z.infer<typeof donationImportRowSchema>;

export const requestDonationImportUploadInputSchema = z.object({
  campaignPeriodId: z.uuid(),
  fileName: fileNameSchema,
  fileSize: z.number().int().min(1).max(DONATION_IMPORT_MAX_BYTES),
});

export const createDonationImportInputSchema = z.object({
  campaignPeriodId: z.uuid(),
  uploadToken: uploadTokenSchema,
});

export type CreateDonationImportInput = z.infer<typeof createDonationImportInputSchema>;

export const donationImportListInputSchema = sortedListRequest(
  DONATION_IMPORT_SORT_FIELDS,
  'createdAt',
  'desc',
)
  .omit({ search: true })
  .extend({
    status: donationImportStatusSchema.optional(),
    campaignId: z.uuid().optional(),
  });

export type DonationImportListInput = z.infer<typeof donationImportListInputSchema>;

/**
 * The commit of a previewed import — docs/decisions/0104 §5. It names the
 * operator the rows are recorded against, when it is not the uploader, and the
 * `updatedAt` the screen showed, so a commit is never made of a preview that
 * has moved since. There is **no notice choice**: an import tells nobody (§7).
 */
export const commitDonationImportInputSchema = z.object({
  id: z.uuid(),
  enteredByUserId: z.uuid().optional(),
  expectedUpdatedAt: z.iso.datetime({ offset: true }),
});

export type CommitDonationImportInput = z.infer<typeof commitDonationImportInputSchema>;

/**
 * A donation import — docs/decisions/0104. Every route is behind
 * `donation.import`, and the service asks `donation.create` beside it, because
 * every imported row is a staff entry.
 */
export const donationImportContract = {
  targets: oc
    .route({ method: 'GET', path: '/donation-imports/targets' })
    .errors(guarded)
    .input(z.object({}))
    .output(z.object({ items: z.array(donationImportTargetSchema) })),

  /**
   * Asks the worker for the season's template — the campaign's published form
   * as a spreadsheet (0104 §2). The answer is the `generated_files` row, which
   * the screen then watches and downloads like any other generated file.
   */
  requestTemplate: oc
    .route({ method: 'POST', path: '/donation-imports/templates' })
    .errors(guarded)
    .input(donationImportTemplateInputSchema)
    .output(generatedFileSchema),

  /**
   * A presigned upload for one filled template, after the season's import
   * gate. Nothing is written: the token names the season, the caller, the
   * declared size and an expiry, signed so `create` can trust it (0104 §3).
   */
  requestUpload: oc
    .route({ method: 'POST', path: '/donation-imports/uploads' })
    .errors(guarded)
    .input(requestDonationImportUploadInputSchema)
    .output(presignedUploadSchema),

  /**
   * The intent row. The server reads the uploaded bytes, admits only an
   * `.xlsx` or `.xls`, hashes them and moves them to a key only it has
   * written, then commits the import `uploaded` and queues the scan and the
   * preview. Nothing becomes a donation here.
   */
  create: oc
    .route({ method: 'POST', path: '/donation-imports' })
    .errors(guarded)
    .input(createDonationImportInputSchema)
    .output(donationImportDetailSchema),

  list: oc
    .route({ method: 'GET', path: '/donation-imports' })
    .errors(guarded)
    .input(donationImportListInputSchema)
    .output(paginated(donationImportSchema)),

  get: oc
    .route({ method: 'GET', path: '/donation-imports/{id}' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(donationImportDetailSchema),

  rows: oc
    .route({ method: 'GET', path: '/donation-imports/{id}/rows' })
    .errors(guarded)
    .input(
      paginationInputSchema.extend({
        id: z.uuid(),
        view: z.enum(DONATION_IMPORT_ROW_VIEWS).default('all'),
      }),
    )
    .output(paginated(donationImportRowSchema)),

  /**
   * The uploader's alone, and only of a preview: `preview → committing`, and
   * the job then creates one donation per valid row, each in its own
   * transaction, through staff entry's own write. Nobody is notified.
   */
  commit: oc
    .route({ method: 'POST', path: '/donation-imports/{id}/commit' })
    .errors(guarded)
    .input(commitDonationImportInputSchema)
    .output(donationImportDetailSchema),

  /** The uploader's alone, and only before anything was created from it. */
  discard: oc
    .route({ method: 'POST', path: '/donation-imports/{id}/discard' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(donationImportDetailSchema),
};
